/**
 * /c/:slug/alumni - Alumni Reconciliation page.
 *
 * Per CHAP-224 (Phase 1Q #201). Replaces the ComingSoon stub on the
 * alumni route. Ports the visual contract of omega-club's
 * AlumniReconciliation.jsx (sources card, summary tiles by action +
 * by classification, paginated row list) onto the multi-tenant
 * chapter-build data model.
 *
 * Sources: GET /api/c/:slug/alumni/sources
 * Diff:    GET /api/c/:slug/alumni/reconciliations?canonical=...&comparison=...
 * Run:     POST /api/c/:slug/alumni/reconcile (admin/team)
 * Resolve: POST /api/c/:slug/alumni/resolutions (admin/team)
 */

import { Fragment, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';
import { useUserRoles } from '../hooks/useUserRoles.js';

interface Source {
  id: string;
  name: string;
  sourceType: string;
  importedAt: string | null;
  status: string;
}

interface SourcesResponse {
  sources: Source[];
}

interface DiffRow {
  id: string;
  chapterInitiationNumber: string | null;
  field: string;
  valueCanonical: string | null;
  valueComparison: string | null;
  diffType: 'agreement' | 'value_mismatch' | 'present_only_canonical' | 'present_only_comparison';
  runAt: string | null;
  // CHAP-242
  recommendedAction?: 'auto_verified_agreement' | 'auto_verified_enrichment' | null;
  resolvedAction?: string | null;
  verificationSource?: 'auto' | 'manual' | 'unverified';
}

// CHAP-224 — the manual per-row/bulk resolution actions the UI emits.
type RowAction = 'accept_canonical' | 'accept_comparison' | 'skip';

interface ReconResponse {
  canonicalSourceId: string;
  comparisonSourceId: string;
  summary: {
    total: number;
    agreement: number;
    value_mismatch: number;
    present_only_canonical: number;
    present_only_comparison: number;
    // CHAP-242 verification rollup
    autoVerifiable?: number;
    autoVerified?: number;
    manualVerified?: number;
  };
  rows: DiffRow[];
}

interface AutoVerifyResult {
  applied: number;
  byAction: { auto_verified_agreement: number; auto_verified_enrichment: number };
  skippedAlreadyResolved: number;
}

interface ReconcileRunResult {
  rowsWritten: number;
  agreementCount: number;
  valueMismatchCount: number;
  onlyCanonicalCount: number;
  onlyComparisonCount: number;
  matchedRecordCount: number;
  unmatchedKeyCanonical: number;
  unmatchedKeyComparison: number;
  runAt: string;
}

const DIFF_BADGE: Record<DiffRow['diffType'], { label: string; cls: string }> = {
  agreement: { label: 'Match', cls: 'bg-green-100 text-green-800' },
  value_mismatch: { label: 'Diff', cls: 'bg-amber-100 text-amber-900' },
  present_only_canonical: { label: 'Canonical only', cls: 'bg-orange-100 text-orange-900' },
  present_only_comparison: { label: 'Comparison only', cls: 'bg-blue-100 text-blue-800' },
};

/** CHAP-242 — auto vs manual verification badge per diff row. */
function VerificationBadge({ row }: { row: DiffRow }) {
  if (row.verificationSource === 'auto') {
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800">Auto &#10003;</span>;
  }
  if (row.verificationSource === 'manual') {
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Manual &#10003;</span>;
  }
  if (row.recommendedAction) {
    return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">Auto-eligible</span>;
  }
  return <span className="text-[10px] text-neutral">n/a</span>;
}

export default function AlumniReconciliation() {
  const { slug } = useParams<{ slug: string }>();
  const tenantSlug = slug ?? '';
  const qc = useQueryClient();
  const roles = useUserRoles();
  const isAdminOrTeam =
    roles.allowedRoles.includes('admin') || roles.allowedRoles.includes('team');

  const [canonical, setCanonical] = useState<string>('');
  const [comparison, setComparison] = useState<string>('');
  const [search, setSearch] = useState('');

  const sourcesQ = useQuery<SourcesResponse, Error>({
    queryKey: ['alumni-sources', tenantSlug],
    queryFn: () => apiGet<SourcesResponse>(`/api/c/${tenantSlug}/alumni/sources`),
  });

  // Default the picker to the two most recent sources (by importedAt)
  // when sources first arrive - saves Cabinet members the picker step.
  const sources = sourcesQ.data?.sources ?? [];
  if (canonical === '' && sources.length >= 2) {
    setCanonical(sources[0].id);
    setComparison(sources[1].id);
  }

  const reconQ = useQuery<ReconResponse, Error>({
    queryKey: ['alumni-recon', tenantSlug, canonical, comparison],
    queryFn: () =>
      apiGet<ReconResponse>(
        `/api/c/${tenantSlug}/alumni/reconciliations?canonical=${encodeURIComponent(canonical)}&comparison=${encodeURIComponent(comparison)}`,
      ),
    enabled: Boolean(canonical && comparison && canonical !== comparison),
  });

  const runReconcile = useMutation({
    mutationFn: () =>
      apiPost<ReconcileRunResult>(`/api/c/${tenantSlug}/alumni/reconcile`, {
        canonicalSourceId: canonical,
        comparisonSourceId: comparison,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alumni-recon', tenantSlug, canonical, comparison] });
    },
  });

  // CHAP-242 — bulk auto-verify the eligible (agreement + enrichment) rows.
  const autoVerify = useMutation({
    mutationFn: () =>
      apiPost<AutoVerifyResult>(`/api/c/${tenantSlug}/alumni/reconcile/auto-verify`, {
        canonicalSourceId: canonical,
        comparisonSourceId: comparison,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alumni-recon', tenantSlug, canonical, comparison] });
    },
  });

  // CHAP-224 — per-row + bulk manual resolution over the existing
  // POST /resolutions endpoint. accept_canonical = "use the canonical
  // (Foundation) value"; accept_comparison = "use the comparison (Pennington)
  // value"; skip = leave as-is.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const resolve = useMutation({
    mutationFn: (v: {
      reconciliationId: string;
      action: RowAction | 'manual_edit';
      before?: unknown;
      after?: unknown;
    }) => apiPost(`/api/c/${tenantSlug}/alumni/resolutions`, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alumni-recon', tenantSlug, canonical, comparison] });
    },
  });
  // CHAP-224 — manual-edit (type a corrected value) + per-record drill-down.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  function saveManualEdit(r: DiffRow) {
    resolve.mutate(
      {
        reconciliationId: r.id,
        action: 'manual_edit',
        before: { canonical: r.valueCanonical, comparison: r.valueComparison },
        after: { value: editValue.trim() },
      },
      { onSuccess: () => setEditingId(null) },
    );
  }
  async function bulkResolve(action: RowAction) {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      for (const id of ids) {
        await apiPost(`/api/c/${tenantSlug}/alumni/resolutions`, {
          reconciliationId: id,
          action,
        });
      }
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['alumni-recon', tenantSlug, canonical, comparison] });
    } finally {
      setBulkBusy(false);
    }
  }

  // Filtered diff rows by search
  const visibleRows = useMemo(() => {
    if (!reconQ.data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return reconQ.data.rows;
    return reconQ.data.rows.filter(
      (r) =>
        r.chapterInitiationNumber?.toLowerCase().includes(q) ||
        r.field.toLowerCase().includes(q) ||
        r.valueCanonical?.toLowerCase().includes(q) ||
        r.valueComparison?.toLowerCase().includes(q),
    );
  }, [reconQ.data, search]);

  const canonicalSrc = sources.find((s) => s.id === canonical);
  const comparisonSrc = sources.find((s) => s.id === comparison);

  // CHAP-247 — download a CSV export (winner | outreach) for the selected pair.
  async function downloadExport(mode: 'winner' | 'outreach') {
    const url = `/api/c/${tenantSlug}/alumni/export?canonical=${encodeURIComponent(canonical)}&comparison=${encodeURIComponent(comparison)}&mode=${mode}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return;
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `alumni-${mode}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  }

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Alumni Reconciliation</h2>
            <p className="text-neutral text-sm mt-1">
              Compare two alumni list sources by chapter initiation number. The
              engine classifies each field as Agreement, Diff, or only-present-
              in-one-source.
            </p>
          </div>
          {isAdminOrTeam && canonical && comparison && canonical !== comparison && (
            <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
              <button
                type="button"
                onClick={() => downloadExport('winner')}
                className="border border-gray-300 text-navy text-sm font-medium px-3 py-2 rounded hover:bg-gray-50"
                title="Reconciled list (winner per record)"
              >
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => downloadExport('outreach')}
                className="border border-orange/40 text-orange text-sm font-medium px-3 py-2 rounded hover:bg-orange/5"
                title="Outreach list (both addresses where different)"
              >
                Outreach CSV
              </button>
              {(reconQ.data?.summary.autoVerifiable ?? 0) > 0 && (
                <button
                  type="button"
                  disabled={autoVerify.isPending}
                  onClick={() => autoVerify.mutate()}
                  className="border border-green-400 text-green-800 text-sm font-medium px-3 py-2 rounded hover:bg-green-50 disabled:opacity-50"
                  title="Auto-verify agreement + one-sided enrichment rows"
                >
                  {autoVerify.isPending
                    ? 'Auto-verifying…'
                    : `Auto-verify all (${reconQ.data?.summary.autoVerifiable})`}
                </button>
              )}
              <button
                type="button"
                disabled={runReconcile.isPending}
                onClick={() => runReconcile.mutate()}
                className="bg-navy text-white text-sm font-medium px-4 py-2 rounded hover:bg-navy/90 disabled:opacity-50"
              >
                {runReconcile.isPending ? 'Reconciling…' : 'Run reconciliation'}
              </button>
            </div>
          )}
        </div>
        {autoVerify.error && (
          <p className="mt-2 text-xs text-red-700">Auto-verify failed: {autoVerify.error.message}</p>
        )}
        {autoVerify.data && (
          <p className="mt-2 text-xs text-green-700">
            Auto-verified {autoVerify.data.applied} rows ({autoVerify.data.byAction.auto_verified_agreement} agreement,{' '}
            {autoVerify.data.byAction.auto_verified_enrichment} enrichment).
            {autoVerify.data.skippedAlreadyResolved > 0 && ` ${autoVerify.data.skippedAlreadyResolved} already resolved.`}
          </p>
        )}
        {runReconcile.error && (
          <p className="mt-2 text-xs text-red-700">
            Reconcile failed: {runReconcile.error.message}
          </p>
        )}
        {runReconcile.data && (
          <p className="mt-2 text-xs text-green-700">
            Wrote {runReconcile.data.rowsWritten} diff rows;{' '}
            {runReconcile.data.matchedRecordCount} records matched.
          </p>
        )}
      </header>

      {/* Source picker */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        {sourcesQ.isLoading && (
          <p className="text-sm text-neutral">Loading sources…</p>
        )}
        {sourcesQ.error && (
          <p className="text-sm text-red-700">
            Failed to load sources: {sourcesQ.error.message}
          </p>
        )}
        {sources.length < 2 && !sourcesQ.isLoading && (
          <p className="text-sm text-neutral italic">
            At least two sources required. Currently {sources.length} loaded.
            {isAdminOrTeam && ' Use the Data Room intake (CHAP-233) to add a source.'}
          </p>
        )}
        {sources.length >= 2 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
            <SourcePicker
              label="Canonical (preferred)"
              value={canonical}
              onChange={setCanonical}
              sources={sources}
            />
            <SourcePicker
              label="Comparison"
              value={comparison}
              onChange={setComparison}
              sources={sources}
            />
          </div>
        )}
      </section>

      {/* Summary tiles */}
      {reconQ.data && (
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 min-w-0">
          <SummaryTile label="Total diffs" value={reconQ.data.summary.total} tone="navy" />
          <SummaryTile label="Agreement" value={reconQ.data.summary.agreement} tone="green" />
          <SummaryTile label="Field diff" value={reconQ.data.summary.value_mismatch} tone="amber" />
          <SummaryTile
            label={canonicalSrc ? `Only ${canonicalSrc.name}` : 'Only canonical'}
            value={reconQ.data.summary.present_only_canonical}
            tone="orange"
          />
        </section>
      )}

      {/* Diff list */}
      {reconQ.isLoading && reconQ.fetchStatus !== 'idle' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
          Loading reconciliation…
        </div>
      )}
      {reconQ.data && reconQ.data.summary.total === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center text-neutral text-sm">
          No reconciliation results yet for this source pair.{' '}
          {isAdminOrTeam ? 'Click "Run reconciliation" above to generate.' : 'Ask a team member to run one.'}
        </div>
      )}

      {reconQ.data && reconQ.data.summary.total > 0 && (
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap min-w-0">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by Zeta #, field, or value"
              className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-1.5 text-sm"
            />
            <span className="text-xs text-neutral flex-shrink-0">
              Showing {visibleRows.length} of {reconQ.data.summary.total}
            </span>
          </div>
          {isAdminOrTeam && selected.size > 0 && (
            <div className="px-3 sm:px-4 py-2 border-b border-gray-100 bg-navy/5 flex items-center gap-2 flex-wrap min-w-0">
              <span className="text-xs font-medium text-navy flex-shrink-0">
                {selected.size} selected
              </span>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => bulkResolve('accept_canonical')}
                title={canonicalSrc ? `Use ${canonicalSrc.name} for all selected` : 'Use canonical value for all selected'}
                className="text-xs px-2.5 py-1 rounded border border-orange/40 text-orange hover:bg-orange/5 disabled:opacity-40"
              >
                Use left (canonical)
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => bulkResolve('accept_comparison')}
                title={comparisonSrc ? `Use ${comparisonSrc.name} for all selected` : 'Use comparison value for all selected'}
                className="text-xs px-2.5 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
              >
                Use right (comparison)
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => bulkResolve('skip')}
                className="text-xs px-2.5 py-1 rounded border border-gray-300 text-neutral hover:bg-gray-50 disabled:opacity-40"
              >
                Skip
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => setSelected(new Set())}
                className="text-xs px-2.5 py-1 rounded text-neutral hover:bg-gray-100 disabled:opacity-40 ml-auto flex-shrink-0"
              >
                Clear
              </button>
              {bulkBusy && <span className="text-xs text-neutral flex-shrink-0">Applying…</span>}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-neutral bg-gray-50">
                <tr>
                  {isAdminOrTeam && (
                    <th className="px-3 py-2 font-medium">
                      <input
                        type="checkbox"
                        aria-label="Select all visible rows"
                        checked={
                          visibleRows.length > 0 &&
                          visibleRows.slice(0, 200).every((r) => selected.has(r.id))
                        }
                        onChange={(e) => {
                          const next = new Set(selected);
                          for (const r of visibleRows.slice(0, 200)) {
                            if (e.target.checked) next.add(r.id);
                            else next.delete(r.id);
                          }
                          setSelected(next);
                        }}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 font-medium">Zeta #</th>
                  <th className="px-3 py-2 font-medium">Field</th>
                  <th className="px-3 py-2 font-medium">
                    {canonicalSrc?.name ?? 'Canonical'}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {comparisonSrc?.name ?? 'Comparison'}
                  </th>
                  <th className="px-3 py-2 font-medium">Class</th>
                  <th className="px-3 py-2 font-medium">Verification</th>
                  {isAdminOrTeam && <th className="px-3 py-2 font-medium">Resolve</th>}
                </tr>
              </thead>
              <tbody>
                {visibleRows.slice(0, 200).map((r) => {
                  const badge = DIFF_BADGE[r.diffType];
                  const recordRows = r.chapterInitiationNumber
                    ? (reconQ.data?.rows ?? []).filter(
                        (x) => x.chapterInitiationNumber === r.chapterInitiationNumber,
                      )
                    : [];
                  return (
                    <Fragment key={r.id}>
                    <tr className="border-b border-gray-50 hover:bg-gray-50 align-top">
                      {isAdminOrTeam && (
                        <td className="px-3 py-1.5">
                          <input
                            type="checkbox"
                            aria-label={`Select ${r.chapterInitiationNumber ?? r.id}`}
                            checked={selected.has(r.id)}
                            onChange={(e) => {
                              const next = new Set(selected);
                              if (e.target.checked) next.add(r.id);
                              else next.delete(r.id);
                              setSelected(next);
                            }}
                          />
                        </td>
                      )}
                      <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                        {r.chapterInitiationNumber ? (
                          <button
                            type="button"
                            onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                            className="underline decoration-dotted underline-offset-2 hover:text-navy"
                            title="Show the full record diff"
                            aria-expanded={expandedId === r.id}
                          >
                            {r.chapterInitiationNumber}
                          </button>
                        ) : (
                          'n/a'
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-neutral">{r.field}</td>
                      <td className="px-3 py-1.5">{r.valueCanonical ?? <span className="text-neutral italic">n/a</span>}</td>
                      <td className="px-3 py-1.5">{r.valueComparison ?? <span className="text-neutral italic">n/a</span>}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <VerificationBadge row={r} />
                      </td>
                      {isAdminOrTeam && (
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          {r.resolvedAction ? (
                            <span className="text-[10px] text-neutral">
                              &#10003; {r.resolvedAction.replace(/_/g, ' ')}
                            </span>
                          ) : editingId === r.id ? (
                            <span className="inline-flex items-center gap-1">
                              <input
                                type="text"
                                value={editValue}
                                autoFocus
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveManualEdit(r);
                                  if (e.key === 'Escape') setEditingId(null);
                                }}
                                aria-label={`Corrected value for ${r.field}`}
                                className="w-36 border border-gray-300 rounded px-1.5 py-0.5 text-[11px]"
                              />
                              <button
                                type="button"
                                disabled={resolve.isPending || editValue.trim() === ''}
                                onClick={() => saveManualEdit(r)}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-40"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingId(null)}
                                className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-neutral hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                title={canonicalSrc ? `Use ${canonicalSrc.name}` : 'Use canonical value'}
                                disabled={bulkBusy || (resolve.isPending && resolve.variables?.reconciliationId === r.id)}
                                onClick={() => resolve.mutate({ reconciliationId: r.id, action: 'accept_canonical' })}
                                className="text-[10px] px-1.5 py-0.5 rounded border border-orange/40 text-orange hover:bg-orange/5 disabled:opacity-40"
                              >
                                Use left
                              </button>
                              <button
                                type="button"
                                title={comparisonSrc ? `Use ${comparisonSrc.name}` : 'Use comparison value'}
                                disabled={bulkBusy || (resolve.isPending && resolve.variables?.reconciliationId === r.id)}
                                onClick={() => resolve.mutate({ reconciliationId: r.id, action: 'accept_comparison' })}
                                className="text-[10px] px-1.5 py-0.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                              >
                                Use right
                              </button>
                              <button
                                type="button"
                                title="Skip this row"
                                disabled={bulkBusy || (resolve.isPending && resolve.variables?.reconciliationId === r.id)}
                                onClick={() => resolve.mutate({ reconciliationId: r.id, action: 'skip' })}
                                className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-neutral hover:bg-gray-50 disabled:opacity-40"
                              >
                                Skip
                              </button>
                              <button
                                type="button"
                                title="Type a corrected value (neither source is right)"
                                disabled={bulkBusy || resolve.isPending}
                                onClick={() => {
                                  setEditingId(r.id);
                                  setEditValue(r.valueCanonical ?? r.valueComparison ?? '');
                                }}
                                className="text-[10px] px-1.5 py-0.5 rounded border border-gray-300 text-neutral hover:bg-gray-50 disabled:opacity-40"
                              >
                                Edit
                              </button>
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                    {expandedId === r.id && (
                      <tr className="bg-gray-50/60 border-b border-gray-100">
                        <td colSpan={isAdminOrTeam ? 8 : 6} className="px-4 py-3">
                          <div className="text-[11px]">
                            <p className="font-medium text-navy mb-1.5">
                              Record {r.chapterInitiationNumber} - full field diff ({recordRows.length}{' '}
                              field{recordRows.length === 1 ? '' : 's'})
                            </p>
                            <table className="w-full">
                              <thead className="text-left text-neutral">
                                <tr>
                                  <th className="pr-3 py-1 font-medium">Field</th>
                                  <th className="pr-3 py-1 font-medium">{canonicalSrc?.name ?? 'Canonical'}</th>
                                  <th className="pr-3 py-1 font-medium">{comparisonSrc?.name ?? 'Comparison'}</th>
                                  <th className="pr-3 py-1 font-medium">Class</th>
                                  <th className="py-1 font-medium">Resolved</th>
                                </tr>
                              </thead>
                              <tbody>
                                {recordRows.map((f) => (
                                  <tr key={f.id} className="border-t border-gray-100 align-top">
                                    <td className="pr-3 py-1 font-mono text-neutral">{f.field}</td>
                                    <td className="pr-3 py-1">{f.valueCanonical ?? <span className="text-neutral italic">n/a</span>}</td>
                                    <td className="pr-3 py-1">{f.valueComparison ?? <span className="text-neutral italic">n/a</span>}</td>
                                    <td className="pr-3 py-1 whitespace-nowrap">
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${DIFF_BADGE[f.diffType].cls}`}>
                                        {DIFF_BADGE[f.diffType].label}
                                      </span>
                                    </td>
                                    <td className="py-1 whitespace-nowrap text-neutral">
                                      {f.resolvedAction ? f.resolvedAction.replace(/_/g, ' ') : 'n/a'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            {visibleRows.length > 200 && (
              <p className="px-3 py-2 text-xs text-neutral italic border-t border-gray-100">
                Showing first 200 rows. Use search to narrow.
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function SourcePicker({
  label,
  value,
  onChange,
  sources,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  sources: Source[];
}) {
  return (
    <label className="block min-w-0">
      <span className="block text-xs font-medium text-navy mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
      >
        {sources.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} ({s.sourceType})
          </option>
        ))}
      </select>
    </label>
  );
}

function SummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'navy' | 'green' | 'amber' | 'orange';
}) {
  const toneCls = {
    navy: 'text-navy',
    green: 'text-green-700',
    amber: 'text-amber-700',
    orange: 'text-orange-700',
  }[tone];
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 min-w-0">
      <p className="text-neutral text-[10px] uppercase tracking-wider">{label}</p>
      <p className={`text-base sm:text-xl font-bold tabular-nums ${toneCls}`}>
        {value.toLocaleString()}
      </p>
    </div>
  );
}
